import type { LimitExtras, LimitPolicy, PlayInterval } from './limits';
import { lisbonDate, lisbonMidnight } from './time';

/**
 * A youth's Arena use over a period, day by day, for the parental dashboard,
 * the weekly email and the PDF report (RF-D1, RF-D4, RF-D6).
 *
 * Same rules as `computeLimitStatus`, looked at afterwards:
 * - minutes count only real session time inside each Lisbon day; a session
 *   past midnight is split between the two days;
 * - a "run" is a chain of sessions with gaps shorter than the break, and the
 *   gaps count towards "in a row";
 * - a day is over the daily limit when its minutes go past the limit with
 *   that day's extras; it is over "in a row" when a run that starts that day
 *   goes past the limit plus that day's monitor grant.
 *
 * Nothing here locks anything (RN-B6): it only tells the guardian what happened.
 */
export interface PlayReportInput {
  intervals: readonly PlayInterval[];
  policy: LimitPolicy;
  /** Extras that applied on a Lisbon date (`YYYY-MM-DD`). */
  extrasOn: (date: string) => LimitExtras;
  /** Lisbon dates, inclusive. */
  from: string;
  to: string;
  /** Open sessions count up to here. */
  now: Date;
}

export interface PlayDay {
  date: string;
  minutes: number;
  /** Sessions that started on this day. */
  sessions: number;
  dailyLimitMinutes: number;
  longestRunMinutes: number;
  overDaily: boolean;
  overContinuous: boolean;
}

export interface PlayTotal {
  minutes: number;
  days: number;
  overLimitDays: number;
}

export interface PlayReport {
  days: PlayDay[];
  weeks: (PlayTotal & { start: string })[];
  months: (PlayTotal & { month: string })[];
  total: PlayTotal;
}

const MINUTE = 60_000;

function round(ms: number): number {
  return Math.round((ms / MINUTE) * 10) / 10;
}

/** `date` plus `n` calendar days. Pure calendar arithmetic, no time zone. */
export function addDays(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** The Monday of the week that holds `date`. */
export function mondayOf(date: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = Sunday
  return addDays(date, -((weekday + 6) % 7));
}

/**
 * The Monday-to-Sunday week before the one that holds `now`, in Lisbon: the
 * period of the weekly email.
 */
export function previousWeek(now: Date): { from: string; to: string } {
  const from = addDays(mondayOf(lisbonDate(now)), -7);
  return { from, to: addDays(from, 6) };
}

export function summarizePlay(input: PlayReportInput): PlayReport {
  const { policy, from, to } = input;
  const nowMs = input.now.getTime();
  const breakMs = policy.breakMinutes * MINUTE;
  const sorted = input.intervals
    .map((i) => ({ start: i.start.getTime(), end: i.end === null ? nowMs : i.end.getTime() }))
    .filter((i) => i.end > i.start)
    .sort((a, b) => a.start - b.start);

  // Runs over everything we were given, so a run that began the evening
  // before `from` is still measured whole.
  const runs: { start: number; end: number }[] = [];
  for (const i of sorted) {
    const last = runs[runs.length - 1];
    if (last && i.start - last.end < breakMs) last.end = Math.max(last.end, i.end);
    else runs.push({ ...i });
  }

  const days: PlayDay[] = [];
  for (let date = from; date <= to; date = addDays(date, 1)) {
    const dayStart = lisbonMidnight(date).getTime();
    const dayEnd = lisbonMidnight(addDays(date, 1)).getTime();
    let playedMs = 0;
    let started = 0;
    for (const i of sorted) {
      const overlap = Math.min(i.end, dayEnd) - Math.max(i.start, dayStart);
      if (overlap > 0) playedMs += overlap;
      if (i.start >= dayStart && i.start < dayEnd) started += 1;
    }
    if (playedMs === 0) continue;

    const extras = input.extrasOn(date);
    const grantMs = extras.grantedMinutes * MINUTE;
    const dailyLimitMs =
      (policy.maxDailyMinutes +
        (extras.isAssociate ? policy.associateExtraMinutes : 0) +
        (extras.hasMerit ? policy.meritExtraMinutes : 0)) *
        MINUTE +
      grantMs;
    const continuousLimitMs = policy.maxContinuousMinutes * MINUTE + grantMs;
    const longestRunMs = Math.max(
      0,
      ...runs.filter((r) => r.start >= dayStart && r.start < dayEnd).map((r) => r.end - r.start),
    );

    days.push({
      date,
      minutes: round(playedMs),
      sessions: started,
      dailyLimitMinutes: round(dailyLimitMs),
      longestRunMinutes: round(longestRunMs),
      // Whole minutes: a few seconds over is not "over".
      overDaily: Math.floor(playedMs / MINUTE) > dailyLimitMs / MINUTE,
      overContinuous: Math.floor(longestRunMs / MINUTE) > continuousLimitMs / MINUTE,
    });
  }

  // Every week and month the period touches, the empty ones included.
  const weekDays = new Map<string, PlayDay[]>();
  const monthDays = new Map<string, PlayDay[]>();
  for (let date = from; date <= to; date = addDays(date, 1)) {
    if (!weekDays.has(mondayOf(date))) weekDays.set(mondayOf(date), []);
    if (!monthDays.has(date.slice(0, 7))) monthDays.set(date.slice(0, 7), []);
  }
  for (const d of days) {
    weekDays.get(mondayOf(d.date))!.push(d);
    monthDays.get(d.date.slice(0, 7))!.push(d);
  }

  return {
    days,
    weeks: [...weekDays].map(([start, list]) => ({ start, ...totalOf(list) })),
    months: [...monthDays].map(([month, list]) => ({ month, ...totalOf(list) })),
    total: totalOf(days),
  };
}

function totalOf(days: readonly PlayDay[]): PlayTotal {
  return {
    minutes: Math.round(days.reduce((sum, d) => sum + d.minutes, 0) * 10) / 10,
    days: days.length,
    overLimitDays: days.filter((d) => d.overDaily || d.overContinuous).length,
  };
}
